/**
 * SurveyPopup UIコンポーネント群。frontend-components.md準拠。
 * BR-U6-1（未ログイン時ログインゲート表示）・BR-U6-8（Shadow DOM隔離）を適用する。
 * design-mockups/company-detail-tag-proposal.html準拠（2026-08-19デザイン更新）:
 * - WidgetPanel共通ラッパー（左下固定パネル）を採用し、未ログイン時はパネル内にログインゲートを重畳する。
 * - アンケート回答後（APPOINTMENT_OFFER）は、同一WidgetPanel内で`bookingSectionUi`の
 *   日程調整UIへ差し替える（従来は別のPreBookingFormを新規に開いていた）。
 */

import { createElement, createShadowHost, applyStylesheet, setText } from "../core/shadowDomUtil";
import { isLoggedIn } from "../core/authState";
import { submitSurveyAnswer } from "./submitSurveyAnswer";
import { trackSurveyAnswer, trackWidgetMinimize } from "../w3-tracking/trackPageEvent";
import { createWidgetPanel, buildSingleCvLeadText, buildWidgetLoginUrl, buildWidgetRegisterUrl, resolveSingleCvPanelPosition, WIDGET_PANEL_STYLES } from "../core-ui/widgetPanelUi";
import { createCalendarModal, CALENDAR_MODAL_STYLES } from "../core-ui/calendarModalUi";
import { MEETING_MODE_LABEL_STYLES, renderMeetingModeLabel } from "../core-ui/meetingModeLabelUi";
import { CALENDAR_ADD_ACTIONS_STYLES } from "../core-ui/calendarAddActionsUi";
import { renderBookingSection } from "../w2-prebooking/bookingSectionUi";
import { BOOKING_CARD_STYLES } from "../w2-prebooking/preBookingFormUi";
import { LOADING_SPINNER_STYLES, renderLoadingSpinner } from "../core-ui/loadingSpinnerUi";
import { DESIGN_TOKEN_CSS, TEXT_PRIMARY, BORDER_DEFAULT, SURFACE_SUBTLE } from "../core-ui/designTokens";
import { CONSENT_TEXT_STYLES } from "../core-ui/consentTextUi";
import { LOGGED_OUT_MOSAIC_STYLES, renderLoggedOutMosaic } from "../core-ui/loggedOutMosaicUi";
import type { ContentDisplayResult, SurveyChoice, PageEventPayload } from "../types";

/** 効果分析ログの共通文脈を ContentDisplayResult から組み立てる（WIDGET_MINIMIZE 等の付与用）。 */
function buildAnalyticsCtx(result: ContentDisplayResult, screenType: string): PageEventPayload {
  return {
    screenType,
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    companyRootId: result.companyRootId,
    prFlg: result.prFlg,
    isMainCv: result.isMainCv,
  };
}

const SURVEY_POPUP_STYLES = `
:host { all: initial; ${DESIGN_TOKEN_CSS} }
${WIDGET_PANEL_STYLES}
${CALENDAR_MODAL_STYLES}
${MEETING_MODE_LABEL_STYLES}
${CALENDAR_ADD_ACTIONS_STYLES}
${BOOKING_CARD_STYLES}
${LOADING_SPINNER_STYLES}
${CONSENT_TEXT_STYLES}
${LOGGED_OUT_MOSAIC_STYLES}

.spica-question { font-size: 15px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 14px; line-height: 1.6; }
.spica-choice-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.spica-choice-item { margin: 0; }
.spica-choice-button {
  width: 100%; border: 1px solid ${BORDER_DEFAULT}; border-radius: 4px; background: #fff;
  padding: 12px 14px; color: ${TEXT_PRIMARY}; cursor: pointer;
  text-align: left; font: inherit; font-size: 13px; font-weight: 500; line-height: 1.5;
}
.spica-choice-button:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }

.spica-link-presentation-button {
  width: 100%; padding: 10px 20px; border: none; border-radius: 4px; background: ${TEXT_PRIMARY};
  color: #fff; cursor: pointer; font-size: 14px; font-weight: 700; margin-top: 12px; font-family: inherit;
  text-align: center;
}
`;

/**
 * SurveyPopupをShadow DOM内に構築する（BR-U6-1/BR-U6-8）。
 *
 * @param screenType 画面種別。リード文の切替に使用する（企業詳細・製品詳細=DETAIL_PAGE、
 *   電子ブック閲覧=EBOOK_VIEWER、お問い合わせ完了=INQUIRY_COMPLETE）
 */
export function renderSurveyPopup(result: ContentDisplayResult, screenType: string): void {
  if (!result.survey) {
    // アンケート未設定（BR-U6-1）: そのまま日程調整（BookingSection）へ進む。
    void renderAppointmentOffer(result, screenType);
    return;
  }

  const { host, shadowRoot } = createShadowHost(`spica-survey-popup-${result.objectRootId}`);
  applyStylesheet(shadowRoot, SURVEY_POPUP_STYLES);

  const lead = buildSingleCvLeadText(screenType, result.objectType);
  const analyticsCtx = buildAnalyticsCtx(result, screenType);
  const panel = createWidgetPanel({
    position: resolveSingleCvPanelPosition(screenType),
    lead,
    ariaLabel: lead,
    minimizable: true,
    // 効果分析ログ（イベントL、BR-AN-14）: 縮小＝離脱意思として WIDGET_MINIMIZE を発火する。
    onMinimize: () => trackWidgetMinimize(analyticsCtx),
  });
  shadowRoot.appendChild(panel.root);
  document.body.appendChild(host);

  if (!isLoggedIn()) {
    // I261-77: 未ログイン時は、アンケートの質問・選択肢を出さず、日程調整画面ベースの固定モザイク
    // （ダミー日程4マス＋案内文＋「ログイン」「新規会員登録」の2ボタン）を表示する。従来の「質問のぼかし描画＋
    // 重畳ログインゲート（showLoginGate）」は廃止した。実データ（質問文・選択肢・形式ラベル）はDOMに入れない。
    // LOGIN_PROMPT は従来どおり描画時に1回発火する（reason=SURVEY_ANSWER を維持）。
    const proposalItem = createElement("div", { className: "spica-proposal-item" });
    const card = createElement("div", { className: "spica-proposal-card" });
    proposalItem.appendChild(card);
    panel.content.appendChild(proposalItem);
    const companyLabelLoggedOut = createElement("div", { className: "spica-company-name" });
    companyLabelLoggedOut.appendChild(document.createTextNode(result.companyName ?? "出展企業"));
    card.appendChild(companyLabelLoggedOut);
    // 要件 FR-6（I261-77）: 企業詳細（COMPANY）は contentName に企業名が入る（ContentNameReader）ため、
    // 未ログイン時は製品名行を出さない（企業名の重複を避ける）。製品詳細等（COMPANY 以外）は contentName（製品名）を出す。
    // bookingSectionUi の未ログイン分岐と同じ扱いにそろえる（アンケートあり経路では製品名が出ない不具合の修正）。
    if (result.contentName && result.objectType !== "COMPANY") {
      card.appendChild(createElement("div", { className: "spica-content-name", text: result.contentName }));
    }
    card.appendChild(renderLoggedOutMosaic({ loginUrl: buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx), registerUrl: buildWidgetRegisterUrl("SURVEY_ANSWER") }));
    return;
  }

  // モック準拠: proposal-item（左の赤い縦線）+ card枠内にコンテンツ配置
  const proposalItem = createElement("div", { className: "spica-proposal-item" });
  const card = createElement("div", { className: "spica-proposal-card" });
  proposalItem.appendChild(card);
  panel.content.appendChild(proposalItem);

  // 企業名＋対応形式ラベルをカード内先頭に表示
  const companyLabel = createElement("div", { className: "spica-company-name" });
  companyLabel.appendChild(document.createTextNode(result.companyName ?? "出展企業"));
  const meetingModeLabel = renderMeetingModeLabel(result.meetingModePolicy);
  if (meetingModeLabel) companyLabel.appendChild(meetingModeLabel);
  card.appendChild(companyLabel);

  renderSurveyQuestion(card, result.survey.question, result.survey.choices, async (choice) => {
    // 効果分析ログ（イベントB、BR-AN-1）: アンケート選択肢を選んだ直後に発火する。
    // 回答内容の自由記述は載せず、選択肢ID・アンケートIDのみ（BR-AN-8）。
    trackSurveyAnswer({
      surveyId: result.survey!.surveyId,
      selectedChoiceId: choice.id,
      objectType: result.objectType,
      objectRootId: result.objectRootId,
      companyRootId: result.companyRootId,
      screenType,
      prFlg: result.prFlg,
      isMainCv: result.isMainCv,
    });
    // 全選択肢ボタンを非活性化（二重送信防止）
    const choiceButtons = card.querySelectorAll<HTMLButtonElement>(".spica-choice-button");
    choiceButtons.forEach((btn) => { btn.disabled = true; });

    // 選択した選択肢の横にスピナーを表示
    const clickedButton = Array.from(choiceButtons).find((btn) => btn.textContent === choice.label);
    const spinner = renderLoadingSpinner({ text: "送信中..." });
    if (clickedButton) clickedButton.insertAdjacentElement("afterend", spinner);

    const submitResult = await submitSurveyAnswer({
      surveyId: result.survey!.surveyId,
      scenarioId: result.scenarioId ?? "",
      objectType: result.objectType,
      objectRootId: result.objectRootId,
      selectedChoiceId: choice.id,
    });

    // スピナー除去・ボタン復元
    spinner.remove();
    choiceButtons.forEach((btn) => { btn.disabled = false; });

    if (submitResult.requiresLogin) {
      // ログインゲート「閉じる」は縮小＋WIDGET_MINIMIZE発火へ統一（BR-U6-14）。host.remove は渡さない。
      panel.showLoginGate(buildWidgetLoginUrl("SURVEY_ANSWER", analyticsCtx));
      return;
    }
    if (!submitResult.success) {
      return;
    }
    if (submitResult.action === "LINK" && submitResult.linkUrl) {
      // カード内のアンケートUIをクリアし、企業名を残してリンクボタンを配置する
      card.textContent = "";
      const companyLabelLink = createElement("div", { className: "spica-company-name" });
      companyLabelLink.appendChild(document.createTextNode(result.companyName ?? "出展企業"));
      const meetingModeLabelLink = renderMeetingModeLabel(result.meetingModePolicy);
      if (meetingModeLabelLink) companyLabelLink.appendChild(meetingModeLabelLink);
      card.appendChild(companyLabelLink);
      renderLinkPresentation(card, submitResult.linkUrl, submitResult.linkDescription ?? null);
    } else if (submitResult.action === "APPOINTMENT_OFFER") {
      // 同一WidgetPanel内で日程調整UI（BookingSection）へ差し替える（カード内）
      card.textContent = "";
      const calendarModal = createCalendarModal();
      shadowRoot.appendChild(calendarModal.element);
      await renderBookingSection(card, panel, calendarModal, {
        memberGroupId: result.memberGroupId ?? "",
        screenType,
        objectType: result.objectType,
        objectRootId: result.objectRootId,
        meetingModePolicy: result.meetingModePolicy,
        prFlg: result.prFlg,
        companyName: result.companyName,
        contentName: result.contentName,
        companyRootId: result.companyRootId,
        isMainCv: result.isMainCv,
        // アンケート回答経由のため、回答ログIDを引き継いでアポイント作成時に送信する（BR-U2-21）。
        surveyResponseLogId: submitResult.surveyResponseLogId,
      });
    }
  });
}

/**
 * アンケート未設定時、SurveyPopup自体を経由せず直接WidgetPanel＋日程調整UIを構築する。
 */
async function renderAppointmentOffer(result: ContentDisplayResult, screenType: string): Promise<void> {
  if (!result.memberGroupId) return;

  const { host, shadowRoot } = createShadowHost(`spica-survey-popup-${result.objectRootId}`);
  applyStylesheet(shadowRoot, SURVEY_POPUP_STYLES);

  const lead = buildSingleCvLeadText(screenType, result.objectType);
  const analyticsCtx = buildAnalyticsCtx(result, screenType);
  const panel = createWidgetPanel({
    position: resolveSingleCvPanelPosition(screenType),
    lead,
    ariaLabel: lead,
    minimizable: true,
    // 効果分析ログ（イベントL、BR-AN-14）: 縮小＝離脱意思として WIDGET_MINIMIZE を発火する。
    onMinimize: () => trackWidgetMinimize(analyticsCtx),
  });
  shadowRoot.appendChild(panel.root);

  const calendarModal = createCalendarModal();
  shadowRoot.appendChild(calendarModal.element);
  document.body.appendChild(host);

  // I261-77: 未ログイン時の固定モザイク＋「ログイン」「新規会員登録」の2ボタンは renderBookingSection が card 内に描画する
  // （従来の panel.showLoginGate 重畳は廃止。LOGIN_PROMPT も renderBookingSection 内で描画時に1回発火）。

  // モック準拠: proposal-item（左の赤い縦線）+ card枠内にコンテンツ配置
  const proposalItem = createElement("div", { className: "spica-proposal-item" });
  const card = createElement("div", { className: "spica-proposal-card" });
  proposalItem.appendChild(card);
  panel.content.appendChild(proposalItem);

  await renderBookingSection(card, panel, calendarModal, {
    memberGroupId: result.memberGroupId,
    screenType,
    objectType: result.objectType,
    objectRootId: result.objectRootId,
    meetingModePolicy: result.meetingModePolicy,
    prFlg: result.prFlg,
    companyName: result.companyName,
    contentName: result.contentName,
    companyRootId: result.companyRootId,
    isMainCv: result.isMainCv,
  });
}

/**
 * SurveyQuestionView: 質問文・選択肢を表示する。
 */
function renderSurveyQuestion(
  container: HTMLElement,
  question: string,
  choices: SurveyChoice[],
  onSelectChoice: (choice: SurveyChoice) => void
): void {
  const questionEl = createElement("p", { className: "spica-question", text: question });
  container.appendChild(questionEl);

  const list = createElement("ul", { className: "spica-choice-list" });
  for (const choice of choices) {
    const item = createElement("li", { className: "spica-choice-item" });
    const button = createElement("button", { className: "spica-choice-button" });
    setText(button, choice.label);
    button.addEventListener("click", () => onSelectChoice(choice));
    item.appendChild(button);
    list.appendChild(item);
  }
  container.appendChild(list);
}

/**
 * LinkPresentationView: linkDescriptionまたはlinkUrlをボタン文言として表示する。
 * linkDescriptionが設定されていればそれを、なければURLをボタンに表示する。
 */
export function renderLinkPresentation(
  container: HTMLElement,
  linkUrl: string,
  linkDescription: string | null
): void {
  const buttonText = linkDescription || linkUrl;
  const button = createElement("button", { className: "spica-link-presentation-button", text: buttonText });
  button.addEventListener("click", () => {
    window.open(linkUrl, "_blank", "noopener,noreferrer");
  });
  container.appendChild(button);
}
